var __read = this && this.__read || function (o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
};
import { useRefContext, useVisibility } from '@shared-ui/ref-context';
import * as React from 'react';
import { UitkSticky } from 'uitk-react-sticky';
import { UitkNavigationType, UitkTab, UitkTabs, UitkTabsType } from 'uitk-react-tabs';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';
import { ViewLarge, Viewport } from '@shared-ui/viewport-context';
import { TripSaveItemWrapper } from '../common/trip-save-item-wrapper';
import { onClickScrollToTarget } from '../../helpers/smooth-scroll';
import { PrimaryButtonTracked } from '../../utils/click-trackers';
import { onVisibilityChangeCallback } from '../../utils/navigation-bar';
import { NavigationRefs } from '../../utils/accessibility';
var STICKY_TAB_OFFSET = 45;
export var tabRef = React.createRef();
var VISIBILITY_OPTIONS = {
  thresholds: [1]
};
function isSectionVisible(elementBounding, offset) {
  var top = elementBounding.top - offset;
  var bottom = elementBounding.bottom - offset;
  return top <= 0 && bottom >= 0;
}
export var TabbedNavigationBar = function (_a) {
  var _b, _c;
  var navigationBarTabList = _a.navigationBarTabList,
    navigationRefs = _a.navigationRefs,
    findTicketsButton = _a.findTicketsButton,
    findTicketsAction = _a.findTicketsAction,
    analytics = _a.analytics,
    saveItem = _a.saveItem,
    saveButtonRef = _a.saveButtonRef;
  var _d = __read(React.useState(navigationBarTabList.length > 0 ? navigationBarTabList[0].label : ''), 2),
    selectedJumplinkTab = _d[0],
    setSelectedJumplinkTabState = _d[1];
  var _e = __read(React.useState(false), 2),
    saveButtonInView = _e[0],
    setSaveButtonInView = _e[1];
  var _f = __read(React.useState(navigationBarTabList.length > 0 ? navigationBarTabList[0].sectionRef : ''), 2),
    selectedJumplinkSectionRef = _f[0],
    setSelectedJumplinkSectionRef = _f[1];
  // Get reference navigation targets.
  var getTargets = useRefContext().getTargets;
  var targets = getTargets(navigationBarTabList === null || navigationBarTabList === void 0 ? void 0 : navigationBarTabList.map(function (tab) {
    return tab.label;
  }));
  var _g = __read(React.useState(true), 2),
    stickyButtonVisible = _g[0],
    setStickyButtonVisible = _g[1];
  // Set the initial active tab to be the first in the list, the list length is
  // checked in the parent component.
  var _h = __read(React.useState(0), 2),
    activeTab = _h[0],
    setActiveTab = _h[1];
  // Memoized tabs list to be used by the visibility observer.
  var tabsList = React.useMemo(function () {
    return navigationBarTabList.map(function (tab) {
      return {
        id: tab.label,
        ref: targets[tab.sectionRef]
      };
    });
  }, [navigationBarTabList, targets]);
  // Control selected tab based on target section visibility.
  var onVisibilityChange = React.useCallback(onVisibilityChangeCallback(setActiveTab, navigationBarTabList), [navigationBarTabList]);
  // Setting observers that listen to changes in visibility of the target sections.
  useVisibility(tabsList, onVisibilityChange, VISIBILITY_OPTIONS);
  var onTabSelect = function (selectedTab) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var selectedTabDetails = navigationBarTabList[selectedTab];
    if (selectedTabDetails === navigationBarTabList[0]) {
      analytics.trackEvent("".concat((_b = (_a = navigationBarTabList[selectedTab]) === null || _a === void 0 ? void 0 : _a.analytics) === null || _b === void 0 ? void 0 : _b.referrerId, ".view.impression"), "".concat((_d = (_c = navigationBarTabList[selectedTab]) === null || _c === void 0 ? void 0 : _c.analytics) === null || _d === void 0 ? void 0 : _d.linkName, ".view.impression"), analytics.omnitureData);
    }
    if (selectedTab !== activeTab && selectedTabDetails) {
      setActiveTab(selectedTab);
      (_f = (_e = targets[selectedTabDetails.sectionRef]) === null || _e === void 0 ? void 0 : _e.current) === null || _f === void 0 ? void 0 : _f.focus();
      if (selectedTabDetails !== navigationBarTabList[0]) {
        analytics.trackEvent("".concat((_h = (_g = navigationBarTabList[selectedTab]) === null || _g === void 0 ? void 0 : _g.analytics) === null || _h === void 0 ? void 0 : _h.referrerId, ".view.impression"), "".concat((_k = (_j = navigationBarTabList[selectedTab]) === null || _j === void 0 ? void 0 : _j.analytics) === null || _k === void 0 ? void 0 : _k.linkName, ".view.impression"), analytics.omnitureData);
      }
    }
  };
  var handleScrollToRef = function () {
    var scrollElementRef = NavigationRefs[(findTicketsAction === null || findTicketsAction === void 0 ? void 0 : findTicketsAction.sectionRef) || ''].current;
    onClickScrollToTarget({
      ref: scrollElementRef
    });
    setTimeout(function () {
      setStickyButtonVisible(false);
    }, 10);
  };
  var onTabClick = function (selectedTabIndex) {
    var _a, _b;
    var navigationTab = navigationBarTabList[selectedTabIndex];
    if (selectedTabIndex === activeTab && !navigationTab) {
      return;
    }
    analytics.clickTracker("".concat((_a = navigationBarTabList[selectedTabIndex].analytics) === null || _a === void 0 ? void 0 : _a.referrerId, ".TabNav"), "".concat((_b = navigationBarTabList[selectedTabIndex].analytics) === null || _b === void 0 ? void 0 : _b.linkName, ".TabNav"), analytics.omnitureData, null, true)();
  };
  React.useEffect(function () {
    var activityDetailPageScrollEventForTabsComponent = function () {
      navigationBarTabList.forEach(function (_a) {
        var _b;
        var sectionRef = _a.sectionRef,
          label = _a.label;
        var refElement = (_b = NavigationRefs[sectionRef]) === null || _b === void 0 ? void 0 : _b.current;
        if (refElement) {
          var elementBounding = refElement.getBoundingClientRect();
          var isCurrentSectionVisible = isSectionVisible(elementBounding, STICKY_TAB_OFFSET);
          setSelectedJumplinkSectionRef(sectionRef);
          if (isCurrentSectionVisible && label !== selectedJumplinkTab) {
            setSelectedJumplinkTabState(label);
          }
          setStickyButtonVisible(selectedJumplinkSectionRef !== (findTicketsAction === null || findTicketsAction === void 0 ? void 0 : findTicketsAction.sectionRef));
        }
      });
    };
    window.addEventListener('scroll', activityDetailPageScrollEventForTabsComponent);
    return function () {
      return window.removeEventListener('scroll', activityDetailPageScrollEventForTabsComponent);
    };
  }, [navigationBarTabList, selectedJumplinkTab]);
  React.useEffect(function () {
    if (saveButtonRef === null || saveButtonRef === void 0 ? void 0 : saveButtonRef.current) {
      new IntersectionObserver(function (entries) {
        return entries.forEach(function (entry) {
          return setSaveButtonInView(!entry.isIntersecting);
        });
      }, {
        rootMargin: '0% 0% -50% 0%',
        threshold: 0.3
      }).observe(saveButtonRef.current);
    } else {
      setSaveButtonInView(true);
    }
  }, [saveButtonRef === null || saveButtonRef === void 0 ? void 0 : saveButtonRef.current]);
  var showSaveButton = saveItem && saveButtonInView;
  return React.createElement(UitkLayoutPosition, {
    type: "relative"
  }, navigationBarTabList.length > 0 && React.createElement(UitkSticky, {
    zIndex: 300
  }, React.createElement(UitkTabs, {
    navigationType: UitkNavigationType.JUMPLINK,
    onTabSelect: onTabSelect,
    selectedTab: activeTab,
    onTabClickCB: onTabClick,
    tabsType: UitkTabsType.NATURAL,
    className: "uitk-card-separator-bottom",
    scrollOffsetSelf: true,
    ref: tabRef
  }, navigationBarTabList.map(function (tab) {
    return React.createElement(UitkTab, {
      id: tab.sectionRef,
      key: "uitk-tab-key-".concat(tab.sectionRef),
      name: tab.label,
      targetRef: navigationRefs[tab.sectionRef],
      targetURI: "#".concat(tab.sectionRef),
      tabLinkAttr: tab.label === selectedJumplinkTab ? {
        'aria-current': 'true'
      } : undefined
    });
  })), React.createElement(Viewport, null, React.createElement(ViewLarge, null, React.createElement(UitkLayoutPosition, {
    type: "absolute",
    position: {
      right: 'zero',
      top: 'zero',
      bottom: 'zero'
    }
  }, React.createElement(UitkSpacing, {
    padding: {
      blockstart: 'one'
    }
  }, React.createElement(UitkLayoutFlex, {
    space: "two",
    justifyContent: "end",
    alignItems: "center",
    blockSize: "full_size"
  }, showSaveButton && React.createElement(UitkLayoutFlexItem, null, React.createElement(TripSaveItemWrapper, {
    saveItem: saveItem
  })), React.createElement(UitkLayoutFlexItem, {
    grow: 0
  }, React.createElement(UitkSpacing, {
    padding: {
      inlineend: 'three'
    }
  }, React.createElement("div", null, stickyButtonVisible && findTicketsButton && findTicketsAction && React.createElement(PrimaryButtonTracked, {
    onClick: handleScrollToRef,
    rfrr: ((_b = findTicketsAction.clientSideAnalytics) === null || _b === void 0 ? void 0 : _b.referrerId) || '',
    linkName: ((_c = findTicketsAction.clientSideAnalytics) === null || _c === void 0 ? void 0 : _c.linkName) || ''
  }, findTicketsButton.primary)))))))))));
};